import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  TimePicker,
  Tooltip,
  notification,
} from 'antd';
import dayjs from 'dayjs';
import { FC, useCallback, useState } from 'react';
import { weekdayToKr } from 'src/constants';

import { AuditLogger } from 'src/utils/audit-logger';
import { formatNumber } from 'src/utils/common';
import { updateCommerceConf } from 'src/utils/firebase/firebase-short-cut';

import { useCommerceConf } from 'src/redux/hooks/useCommerceConf';

import classes from './AppConfig.module.scss';

const auditLogger = AuditLogger.getInstance();

type OperationFormValue = {
  openAt: dayjs.Dayjs;
  closeAt: dayjs.Dayjs;
  sun: number;
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  deliveryFee: number;
  minAmountForFreeDelivery: number;
};

type UrgentFormValue = {
  isClosed: boolean;
  msgClosed: string;
};

type BasicFormValue = {
  notice: string;
};

type GlobalNoticeFormValue = {
  id: string;
  message: string;
  variant: 'info' | 'warning';
  dismissible: boolean;
};

const timeFormat = 'HH';
const formLayout = {
  content: {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
  },
  tail: {
    wrapperCol: { offset: 8, span: 16 },
  },
};

const AppConfig: FC = () => {
  const { commerceConf } = useCommerceConf();
  const [isFormLocked, setIsFormLocked] = useState<boolean>(true);
  const [operationForm] = Form.useForm();
  const [urgentForm] = Form.useForm();
  const [basicForm] = Form.useForm();
  const [globalNoticeForm] = Form.useForm();

  const toggleForm = useCallback((isLocked: boolean) => {
    setIsFormLocked(isLocked);
    if (!isLocked) {
      auditLogger.log({
        action: 'UPDATE',
        entity: 'config',
        entityId: 'commerceConf',
        summary: '운영 설정 잠금 해제',
      });
    }
  }, []);

  const onSubmitOperation = useCallback(async (values: OperationFormValue) => {
    const openAt = values.openAt.hour();
    const closeAt = values.closeAt.hour();
    const deliveryDateRules = [values.sun, values.mon, values.tue, values.wed, values.thu, values.fri, values.sat];
    const deliveryFee = values.deliveryFee;
    const minAmountForFreeDelivery = values.minAmountForFreeDelivery;
    try {
      await updateCommerceConf(
        {
          openAt,
          closeAt,
          deliveryDateRules,
          deliveryFee,
          minAmountForFreeDelivery,
        },
        { audit: { suppress: true } }
      );
      notification.success({
        message: '반영 완료',
      });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'config',
        entityId: 'commerceConf',
        summary: '운영 설정 변경 (운영시간, 배송비, 배송일 규칙)',
        slack: { channel: '4-앱-알림' },
      });
    } catch (error) {
      console.error(error);
      notification.error({
        message: '에러 발생',
        description: `에러: ${error}`,
      });
    }
  }, []);

  const onSubmitUrgent = useCallback(async (values: UrgentFormValue) => {
    try {
      await updateCommerceConf(
        {
          closed: {
            isClosed: values.isClosed,
            msgClosed: values.msgClosed,
          },
        },
        { audit: { suppress: true } }
      );
      notification.success({
        message: '반영 완료',
      });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'config',
        entityId: 'commerceConf',
        summary: `긴급 설정 변경 - 서비스 ${values.isClosed ? '중지' : '재개'}`,
        changes: [{ field: 'isClosed', before: commerceConf?.closed.isClosed, after: values.isClosed }],
        slack: { channel: '4-앱-알림' },
      });
    } catch (error) {
      console.error(error);
      notification.error({
        message: '에러 발생',
        description: `에러: ${error}`,
      });
    }
  }, []);

  const onSubmitBasic = useCallback(async (values: BasicFormValue) => {
    try {
      await updateCommerceConf(
        {
          notice: values.notice,
        },
        { audit: { suppress: true } }
      );
      notification.success({
        message: '반영 완료',
      });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'config',
        entityId: 'commerceConf',
        summary: '공지사항 변경',
        changes: [{ field: 'notice', before: commerceConf?.notice, after: values.notice }],
        slack: { channel: '4-앱-알림' },
      });
    } catch (error) {
      console.error(error);
      notification.error({
        message: '에러 발생',
        description: `에러: ${error}`,
      });
    }
  }, []);

  const onDeleteNotice = useCallback(async () => {
    try {
      await updateCommerceConf(
        {
          notice: null,
        },
        { audit: { suppress: true } }
      );
      notification.success({
        message: '반영 완료',
      });
      auditLogger.log({
        action: 'DELETE',
        entity: 'config',
        entityId: 'commerceConf',
        summary: '공지사항 삭제',
        changes: [{ field: 'notice', before: commerceConf?.notice, after: null }],
        slack: { channel: '4-앱-알림' },
      });
    } catch (error) {
      console.error(error);
      notification.error({
        message: '에러 발생',
        description: `에러: ${error}`,
      });
    }
  }, []);

  const onSubmitGlobalNotice = useCallback(async (values: GlobalNoticeFormValue) => {
    const before = commerceConf?.globalNotice ?? null;
    const after = {
      id: values.id.trim(),
      message: values.message,
      variant: values.variant,
      dismissible: values.dismissible,
    };
    try {
      await updateCommerceConf({ globalNotice: after }, { audit: { suppress: true } });
      notification.success({
        message: '반영 완료',
      });
      auditLogger.log({
        action: 'UPDATE',
        entity: 'config',
        entityId: 'commerceConf',
        summary: '전역 공지 변경',
        changes: [{ field: 'globalNotice', before, after }],
        slack: { channel: '4-앱-알림' },
      });
    } catch (error) {
      console.error(error);
      notification.error({
        message: '에러 발생',
        description: `에러: ${error}`,
      });
    }
  }, []);

  const onDeleteGlobalNotice = useCallback(async () => {
    try {
      await updateCommerceConf({ globalNotice: null }, { audit: { suppress: true } });
      notification.success({
        message: '반영 완료',
      });
      auditLogger.log({
        action: 'DELETE',
        entity: 'config',
        entityId: 'commerceConf',
        summary: '전역 공지 내림',
        changes: [{ field: 'globalNotice', before: commerceConf?.globalNotice ?? null, after: null }],
        slack: { channel: '4-앱-알림' },
      });
    } catch (error) {
      console.error(error);
      notification.error({
        message: '에러 발생',
        description: `에러: ${error}`,
      });
    }
  }, []);

  if (!commerceConf) {
    return <div>현재 설정을 불러오지 못했습니다.</div>;
  }

  return (
    <div className={classes.appConfigContainer}>
      <div>
        <h2>기본 설정</h2>
        <div className={classes.formContainer}>
          <Form form={basicForm} {...formLayout.content} layout='horizontal' onFinish={onSubmitBasic}>
            <Form.Item name='notice' label='메인 화면 한 줄 공지' initialValue={commerceConf.notice}>
              <Input />
            </Form.Item>
            <Form.Item {...formLayout.tail}>
              <Button type='primary' htmlType='submit'>
                변경
              </Button>
              <Button danger onClick={onDeleteNotice} style={{ marginLeft: 8 }}>
                삭제
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
      <div>
        <h2>전역 공지</h2>
        <div className={classes.formContainer}>
          <Form form={globalNoticeForm} {...formLayout.content} layout='horizontal' onFinish={onSubmitGlobalNotice}>
            <Form.Item
              name='id'
              label='버전 키 (id)'
              initialValue={commerceConf.globalNotice?.id ?? ''}
              rules={[{ required: true, message: '버전 키를 입력하세요.' }]}
              tooltip='바꾸면 "다시 보지 않기"한 사용자에게도 다시 노출됩니다. 같은 공지를 수정만 할 땐 유지하세요.'>
              <Input placeholder='예: v2-migration-2026-06' />
            </Form.Item>
            <Form.Item
              name='message'
              label='메시지'
              initialValue={commerceConf.globalNotice?.message ?? ''}
              rules={[{ required: true, message: '메시지를 입력하세요.' }]}>
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name='variant' label='종류' initialValue={commerceConf.globalNotice?.variant ?? 'info'}>
              <Select
                options={[
                  { value: 'info', label: 'info (안내)' },
                  { value: 'warning', label: 'warning (경고)' },
                ]}
              />
            </Form.Item>
            <Form.Item
              name='dismissible'
              label='닫기 허용'
              valuePropName='checked'
              initialValue={commerceConf.globalNotice?.dismissible ?? true}
              tooltip='끄면 사용자가 공지를 닫을 수 없습니다.(시간 초과 시 자동 닫힘)'>
              <Switch />
            </Form.Item>
            <Form.Item {...formLayout.tail}>
              <Button type='primary' htmlType='submit'>
                저장
              </Button>
              <Button danger onClick={onDeleteGlobalNotice} style={{ marginLeft: 8 }}>
                공지 내리기
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
      <div>
        <h2>운영 설정</h2>
        <div>
          <Checkbox checked={isFormLocked} onChange={(e) => toggleForm(e.target.checked)}>
            잠금
          </Checkbox>
        </div>
        <div className={`${classes.formContainer} ${isFormLocked ? classes.locked : ''}`}>
          <Form
            form={operationForm}
            layout='horizontal'
            {...formLayout.content}
            disabled={isFormLocked}
            onFinish={onSubmitOperation}>
            <Form.Item
              name='openAt'
              label='주문 시작 시간'
              initialValue={dayjs(`${String(commerceConf.openAt).padStart(2, '0')}:00`, timeFormat)}>
              <TimePicker format={timeFormat} />
            </Form.Item>
            <Form.Item
              name='closeAt'
              label='주문 마감 시간'
              initialValue={dayjs(`${String(commerceConf.closeAt).padStart(2, '0')}:00`, timeFormat)}>
              <TimePicker format={timeFormat} />
            </Form.Item>
            <Form.Item label='배송에 걸리는 기간(일)'>
              <Tooltip title='1: 다음날 도착, 2: 다다음날 도착, 3: 3일 후 도착...'>
                <QuestionCircleOutlined />
              </Tooltip>
            </Form.Item>
            <Row>
              <Col span={8}></Col>
              <Col span={16}>
                <Row>
                  {Object.entries(weekdayToKr).map(([name, label], idx) => (
                    <Col key={name} span={6}>
                      <Form.Item name={name} initialValue={commerceConf.deliveryDateRules[idx]}>
                        <InputNumber addonBefore={label} min={1} max={10} />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
            <Form.Item name='deliveryFee' label='배송비' initialValue={commerceConf.deliveryFee}>
              <InputNumber min={0} formatter={(n) => formatNumber(n ?? 0)} addonAfter='원' />
            </Form.Item>
            <Form.Item
              name='minAmountForFreeDelivery'
              label='배송비 면제를 위한 최소 주문 금액'
              initialValue={commerceConf.minAmountForFreeDelivery}>
              <InputNumber min={0} formatter={(n) => formatNumber(n ?? 0)} addonAfter='원' />
            </Form.Item>
            <Form.Item {...formLayout.tail}>
              <Button type='primary' htmlType='submit'>
                변경
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
      <div>
        <h2>긴급 설정</h2>
        <div className={classes.formContainer}>
          <Form form={urgentForm} {...formLayout.content} layout='horizontal' onFinish={onSubmitUrgent}>
            <Form.Item
              name='isClosed'
              label='서비스 일시 중지'
              valuePropName='checked'
              initialValue={commerceConf.closed.isClosed}>
              <Switch />
            </Form.Item>
            <Form.Item name='msgClosed' label='서비스 일시 중지 메시지' initialValue={commerceConf.closed.msgClosed}>
              <Input />
            </Form.Item>
            <Form.Item {...formLayout.tail}>
              <Button type='primary' htmlType='submit'>
                변경
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default AppConfig;
